/**
 * InviteManagerDrawer — convidar gerente por email.
 *
 * Regras (V2 §3.1 + §3.4 + V2_029 UNIQUE INDEX):
 *   - 1 gerente ATIVO por produto.
 *   - Se já houver, backend retorna 409 CONFLICT com mensagem amigável.
 *
 * Fluxo:
 *   1. Email + debounce 500ms → lookup-by-email.
 *   2. Opcional: override de comissão (%) + permissão de gerenciar afiliados.
 *   3. POST /api/v2/managers/invite.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { managersV2API } from '../../../services/api/v2/managers';
import { usersV2API } from '../../../services/api/v2/users';
import { parseApiError, scrollToFirstError } from '../../../utils/apiErrors';
import Drawer from './Drawer';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = {
  email: '',
  commission_value_override: '',
  can_manage_affiliates: true,
};

const InviteManagerDrawer = ({ isOpen, onClose, productId, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');
  const [lookup, setLookup] = useState({ status: 'idle', exists: null, name: null });
  const lookupTimer = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm(EMPTY);
    setErrors({});
    setTopError('');
    setLookup({ status: 'idle', exists: null, name: null });
  }, [isOpen]);

  useEffect(() => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    const email = form.email.trim().toLowerCase();
    if (!email || !EMAIL_REGEX.test(email)) {
      setLookup({ status: 'idle', exists: null, name: null });
      return;
    }
    setLookup({ status: 'loading', exists: null, name: null });
    lookupTimer.current = setTimeout(async () => {
      try {
        const res = await usersV2API.lookupByEmail(email);
        const data = res.data || {};
        setLookup({
          status: 'done',
          exists: !!data.exists,
          name: data.name || null,
        });
      } catch {
        setLookup({ status: 'error', exists: null, name: null });
      }
    }, 500);
    return () => clearTimeout(lookupTimer.current);
  }, [form.email]);

  const update = (f, v) => {
    setForm((p) => ({ ...p, [f]: v }));
    setErrors((p) => ({ ...p, [f]: undefined }));
  };

  const validate = () => {
    const err = {};
    const email = form.email.trim();
    if (!email) err.email = 'Email é obrigatório';
    else if (!EMAIL_REGEX.test(email)) err.email = 'Email inválido';
    if (form.commission_value_override !== '') {
      const n = parseFloat(form.commission_value_override);
      if (Number.isNaN(n) || n < 0) err.commission_value_override = 'Use um número >= 0';
      else if (n > 100) err.commission_value_override = 'Máximo 100%';
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    try {
      setSubmitting(true);
      setTopError('');
      const payload = {
        product_id: productId,
        email: form.email.trim().toLowerCase(),
        can_manage_affiliates: !!form.can_manage_affiliates,
      };
      if (form.commission_value_override !== '') {
        payload.commission_value_override = parseFloat(form.commission_value_override);
      }
      const res = await managersV2API.invite(payload);
      const data = res.data || {};
      toast.success(`Convite enviado para ${payload.email}`);
      onSaved?.(data.manager || null);
      onClose?.();
    } catch (err) {
      const { fieldErrors, message } = parseApiError(err, 'Erro ao enviar convite');
      if (Object.keys(fieldErrors).length) {
        setErrors((p) => ({ ...p, ...fieldErrors }));
        scrollToFirstError(fieldErrors);
      }
      setTopError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Convidar gerente"
      subtitle="Apenas 1 gerente ativo por produto"
      icon="mdi:account-tie-outline"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Enviar convite"
    >
      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Email do gerente</span>
        <input
          className="drawer-input"
          type="email"
          value={form.email}
          onChange={(e) => update('email', e.target.value)}
          placeholder="gerente@email.com"
          data-field="email"
          autoComplete="off"
        />
        <LookupHint lookup={lookup} email={form.email} />
        {errors.email && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.email}
          </span>
        )}
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Comissão override (%)</span>
        <div className="drawer-input--with-suffix">
          <input
            className="drawer-input"
            type="text"
            inputMode="decimal"
            value={form.commission_value_override}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
              update('commission_value_override', cleaned);
            }}
            placeholder="Deixe vazio pra usar o padrão do produto"
            data-field="commission_value_override"
          />
          <span className="drawer-input-suffix">%</span>
        </div>
        <span className="drawer-help">
          Opcional. Sobrescreve a % padrão de comissão de gerente do produto.
        </span>
        {errors.commission_value_override && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.commission_value_override}
          </span>
        )}
      </div>

      <div className="drawer-switch">
        <div className="drawer-switch__label">
          <span className="drawer-switch__label-title">Pode gerenciar afiliados</span>
          <span className="drawer-switch__label-desc">
            Aprovar / rejeitar / suspender afiliados do produto.
          </span>
        </div>
        <button
          type="button"
          className={`drawer-switch__input ${form.can_manage_affiliates ? 'is-on' : ''}`}
          onClick={() => update('can_manage_affiliates', !form.can_manage_affiliates)}
          aria-pressed={form.can_manage_affiliates}
        />
      </div>
    </Drawer>
  );
};

const LookupHint = ({ lookup, email }) => {
  const isValid = email.trim() && EMAIL_REGEX.test(email.trim());
  if (!isValid) return null;
  if (lookup.status === 'loading') {
    return (
      <span className="drawer-help" style={{ color: '#6b7280' }}>
        <Icon icon="mdi:loading" className="spin" width="12" /> Verificando…
      </span>
    );
  }
  if (lookup.status === 'done' && lookup.exists) {
    return (
      <span className="drawer-help" style={{ color: '#047857', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Icon icon="mdi:check-circle" width="12" />
        Usuário encontrado{lookup.name ? `: ${lookup.name}` : ''}
      </span>
    );
  }
  if (lookup.status === 'done' && !lookup.exists) {
    return (
      <span className="drawer-help" style={{ color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Icon icon="mdi:email-outline" width="12" />
        Será criado um convite por email
      </span>
    );
  }
  return null;
};

export default InviteManagerDrawer;
